import {
  IconBox,
  IconBriefcase,
  IconBuilding,
  IconCaretLeft,
  IconCurrencyDollar,
  IconFile,
  IconFileReport,
  IconKey,
  IconListDetails,
  IconMail,
  IconProps,
  IconSearch,
  IconSettings,
  IconSparkles,
  IconTemplate,
  IconUser,
} from "@tabler/icons-react";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { useAuthorization } from "~/contexts/authorizationProvider";
import { useFeatureFlags } from "~/contexts/featureFlagProvider";
import { useOrg } from "~/contexts/orgProvider";
import { ledgerTransactionViewPermissions } from "~/modules/accounting/ledger-transaction-access";
import { AUTOMATIONS_BASE_PATH } from "~/modules/automations/automation-routes";
import { useOptInOrderDocumentSettingsNavLinks } from "~/modules/orders/order-menu-document-extensions";
import { outputTemplateAccessPermissions } from "~/modules/output-templates/output-template-access";
import { OUTPUT_TEMPLATES_BASE_PATH } from "~/modules/output-templates/output-template-routes";
import { useOutputTemplatesEnabled } from "~/modules/output-templates/useOutputTemplatesEnabled";
import { canAccessPricingEngine, PricingEnginePermission } from "~/modules/pricing-engine/permissions";
import { useWorkOrderFlags } from "~/modules/work-orders/hooks/useWorkOrderFlags";
import {
  featureFlagAiEnabled,
  featureFlagStatementMatchEnabled,
  isMultiCurrencyDefaultEnabled,
  isPlaidBankFeedDefaultEnabled,
  isStatementMatchDefaultEnabled,
  multiCurrencyEnabled,
  plaidBankFeedEnabled,
  reportsV2Enabled,
  salesOrderItemLabelsEnabled,
} from "~/shared/constants/feature-flag.constants";
import { workOrderNavEntries } from "~/shared/constants/work-order-flags";
import {
  AccountsPayableCreditNotePermission,
  AccountsPayablePaymentPermission,
  AccountsReceivableCreditNotePermission,
  AccountsReceiveablePaymentPermission,
  BillPermission,
  CustomerPermission,
  DocumentsSettingsPermission,
  InvoicePermission,
  LedgerAccountPermission,
  LotPermission,
  NotePermission,
  OrderPermission,
  ProductPermission,
  PurchaseOrderPermission,
  ReconciliationPermission,
  ReportPermission,
  SupplierPermission,
  SystemPermission,
  WorkOrderPermission,
} from "~/shared/types/RBACPermission";
import {
  Accordion,
  APPLICATION_FRAME_HEADER_HEIGHT,
  BetaDot,
  Flex,
  Group,
  mantineColorRamps,
  PrimaryNavigation,
  PrimaryNavigationButton,
  PrimaryNavigationCollapseButton,
  PrimaryNavigationFooter,
  PrimaryNavigationLink,
  PrimaryNavigationRailGroup,
  PrimaryNavigationRailMenuLink,
  PrimaryNavigationSection,
  Text,
  TextInput,
  tokens,
  useHotkeys,
  useLocalStorage,
} from "~/ui";
import { api } from "~/utils/api";
import { createLeadingPathRegex, useOrgRouter } from "~/utils/paths";
import { PlatformBillingPermission } from "~/utils/types/platform-billing-permission";

import { MenuContext } from "../AppContainer";
import { OrgLink } from "../OrgLink";

import { AccountActions } from "./AccountActions";
import { buildReceivablePayableNavLinks } from "./finance-accounting-nav-links";
import navbarStyles from "./Navbar.module.css";
import { currentNavGroupLabel, NOTES_NAV_ACTIVE_REGEX, withOpenGroup } from "./navbar-rail";
import { filterSettingsNavLinks, type SettingsNavSearchLink } from "./settings-nav-search";
import { SETTINGS_SEARCH_WORDS } from "./settings-search-words";

type NavBarLink = {
  label: string;
  icon: React.ComponentType<IconProps>;
  links: { label: string; href: string; activeRegex: RegExp; disabled?: boolean }[];
};

/** A Settings menu link; `searchWords` is required so a new Settings page cannot ship without the words that find it. */
type SettingsNavLink = NavBarLink["links"][number] & SettingsNavSearchLink;

type SettingsNavBarLink = Omit<NavBarLink, "links"> & { links: SettingsNavLink[] };

/**
 * Routes whose navigation link carries the shared beta dot, matching the Beta badge on their page header.
 *
 * Custom Outputs is listed by its constant rather than a literal: the dot is matched on the href
 * string, so the set and the link have to move together when the route does (SC-3654).
 */
const BETA_NAV_HREFS: ReadonlySet<string> = new Set(["/work-orders-v2", "/order-flow", OUTPUT_TEMPLATES_BASE_PATH]);

type Props = {
  /** Resolved by AppContainer before it mounts the navigation, so the first render already knows. */
  isDesktop: boolean;
  onClose: () => void;
};

export const Navbar = ({ isDesktop, onClose }: Props): JSX.Element => {
  const { hasPermission, isSuperAdmin } = useAuthorization();
  const { getFeatureFlag } = useFeatureFlags();
  const { navbarOpened, portfolioViewBy, priceBookViewBy } = useContext(MenuContext);
  const [navbarSelections, setNavbarSelections] = useLocalStorage<string[]>({ key: "navbarSelections", defaultValue: [] });
  // Read before the first paint, so a collapsed sidebar never draws open first on load.
  const [savedCollapsed, setSavedCollapsed] = useLocalStorage<boolean>({ key: "navbarCollapsed", defaultValue: false, readOnMount: true });
  const router = useOrgRouter();

  const isOrderItemLabelEnabled = getFeatureFlag(salesOrderItemLabelsEnabled, false);
  const isReportsV2Enabled = getFeatureFlag(reportsV2Enabled, false);
  const isAssistantEnabled = getFeatureFlag(featureFlagAiEnabled, false);
  const isStatementMatchEnabled = getFeatureFlag(featureFlagStatementMatchEnabled, isStatementMatchDefaultEnabled());
  const workOrderFlags = useWorkOrderFlags();
  // Memoized because it feeds the coreNavLinks dependency list — a fresh array every render would
  // rebuild the whole nav tree.
  // biome-ignore lint/correctness/useExhaustiveDependencies: memo keys on scalar WO flags, not the flags object
  const workOrderLinks = useMemo(
    () => workOrderNavEntries(workOrderFlags),
    [workOrderFlags.wov1, workOrderFlags.wov2, workOrderFlags.variablePackagesOnLot, workOrderFlags.wov1CoercedByNoEngine],
  );
  const isMultiCurrencyEnabled = getFeatureFlag(multiCurrencyEnabled, isMultiCurrencyDefaultEnabled());
  const isPlaidBankFeedEnabled = getFeatureFlag(plaidBankFeedEnabled, isPlaidBankFeedDefaultEnabled());
  // Per-org beta gate, independent of the permission below: the flag says whether the org is in
  // the beta, the permission says who inside it may use the builder. The entry needs both.
  const isOutputTemplatesEnabled = useOutputTemplatesEnabled();
  const multiOrgWorkspaceAccess = api.multiOrgWorkspace.currentAccess.useQuery(undefined, { staleTime: 60_000 });
  const hasMultiOrgWorkspaceAccess = (multiOrgWorkspaceAccess.data?.length ?? 0) > 0;
  const { getOrgSetting, orgBaseCurrencyCode } = useOrg();
  const optInDocumentSettingsLinks = useOptInOrderDocumentSettingsNavLinks(getOrgSetting, orgBaseCurrencyCode);

  const hasCustomerViewPermission = hasPermission([CustomerPermission.view, CustomerPermission.viewSalesPerson]);
  const hasSalesOrderViewPermission = hasPermission([OrderPermission.view, OrderPermission.viewAssigned, OrderPermission.viewSalesPerson]);
  /** SC-2390 proposals: unrestricted `order.view` only — see the Order Proposals entry below. */
  const hasReportViewPermission = hasPermission([
    ReportPermission.balanceSheetCreate,
    ReportPermission.incomeStatementCreate,
    ReportPermission.transactionsCreate,
    ReportPermission.trialBalanceCreate,
  ]);
  const hasIncomeStatementPermission = hasPermission([ReportPermission.incomeStatementCreate]);
  const hasReconciliationViewPermission = hasPermission([ReconciliationPermission.view]);
  const hasLedgerTransactionViewPermission = hasPermission(ledgerTransactionViewPermissions);
  const hasLedgerAccountViewPermission = hasPermission([LedgerAccountPermission.view]);
  const hasAccountsReceivableViewPermission = hasPermission([
    InvoicePermission.view,
    AccountsReceivableCreditNotePermission.view,
    AccountsReceiveablePaymentPermission.view,
  ]);
  const hasAccountsPayableViewPermission = hasPermission([
    BillPermission.view,
    AccountsPayableCreditNotePermission.view,
    AccountsPayablePaymentPermission.view,
  ]);
  const hasProductsViewAllPermission = hasPermission([ProductPermission.viewAll]);
  const hasOutputTemplatesPermission = hasPermission(outputTemplateAccessPermissions);
  const hasLotViewPermission = hasPermission([LotPermission.view]);
  const hasSupplierViewPermission = hasPermission([SupplierPermission.view]);
  const hasPurchaseOrderViewPermission = hasPermission([PurchaseOrderPermission.view]);
  const hasWorkOrderViewPermission = hasPermission([WorkOrderPermission.view]);
  const hasSettingsViewPermission = hasPermission([SystemPermission.viewSettings]);
  const hasDocumentsSettingsPermission = hasPermission([DocumentsSettingsPermission.update]);
  const hasNotesAdminManagePermission = hasPermission([NotePermission.adminManage]);
  const hasPlatformBillingViewPermission = hasPermission([PlatformBillingPermission.view]);
  const hasPricingEngineViewPermission = hasPermission([PricingEnginePermission.view]);
  const platformBillingAvailability = api.platformBilling.availability.useQuery(undefined, {
    enabled: hasPlatformBillingViewPermission,
    staleTime: 300_000,
  });
  const hasSalesPerformanceViewPermission = hasPermission([
    ReportPermission.salesPerformanceView,
    ReportPermission.salesPerformanceViewSalesPerson,
  ]);

  const onSettingsPage = router.pathname.split("/")[2] === "settings";
  const [showSettings, setShowSettings] = useState(onSettingsPage);
  const [settingsQuery, setSettingsQuery] = useState("");
  // Opening Settings from the account menu on desktop puts the cursor in the search box. A Settings page that
  // loads keeps focus on its own content, and the mobile drawer skips it so the keyboard does not cover the menu.
  const [focusSettingsSearch, setFocusSettingsSearch] = useState(false);
  const settingsScrollViewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setShowSettings(router.pathname.split("/")[2] === "settings");
  }, [router.pathname]);

  // Leaving the Settings menu, by its back row or by going to a page outside Settings, drops the search.
  useEffect(() => {
    if (!showSettings) {
      setSettingsQuery("");
      setFocusSettingsSearch(false);
    }
  }, [showSettings]);

  const changeSettingsQuery = (query: string) => {
    setSettingsQuery(query);
    // A new query starts the narrowed list from the top, wherever the menu was scrolled.
    if (settingsScrollViewportRef.current) {
      settingsScrollViewportRef.current.scrollTop = 0;
    }
  };

  const coreNavLinks: NavBarLink[] = useMemo(() => {
    const productItems: NavBarLink = {
      label: "Products",
      icon: IconBox,
      links: [],
    };

    if (hasProductsViewAllPermission) {
      productItems.links.push({
        label: "Catalog",
        href: "/products",
        activeRegex: createLeadingPathRegex("/products", { wildcardPathEnd: true }),
      });
    }
    if (hasLotViewPermission) {
      productItems.links.push({
        label: "Inventory",
        href: "/inventory",
        activeRegex: createLeadingPathRegex("/inventory", { wildcardPathEnd: true }),
      });
    }
    if (canAccessPricingEngine(hasPricingEngineViewPermission)) {
      productItems.links.push({
        label: "Pricing Engine",
        href: "/pricing-engine",
        activeRegex: createLeadingPathRegex("/pricing-engine", { wildcardPathEnd: true }),
      });
    }
    productItems.links.push({
      label: "Price Book",
      href: `/price-book${
        priceBookViewBy
          ?.map(view => `viewBy=${view}`)
          .join("&")
          .replace(/^/, "?") ?? ""
      }`,
      activeRegex: createLeadingPathRegex("/price-book", { wildcardPathEnd: true }),
    });
    if (hasSupplierViewPermission) {
      productItems.links.push({
        label: "Suppliers",
        href: "/suppliers",
        activeRegex: createLeadingPathRegex("/suppliers", { wildcardPathEnd: true }),
      });
    }
    if (hasPurchaseOrderViewPermission) {
      productItems.links.push({
        label: "Purchase Orders",
        href: "/purchase-orders",
        activeRegex: createLeadingPathRegex("/purchase-orders", { wildcardPathEnd: true }),
      });
    }
    if (hasWorkOrderViewPermission) {
      for (const link of workOrderLinks) {
        productItems.links.push({
          label: link.label,
          href: link.href,
          activeRegex: createLeadingPathRegex(link.href, { wildcardPathEnd: true }),
        });
      }
    }
    if (hasSalesOrderViewPermission || hasPurchaseOrderViewPermission || hasWorkOrderViewPermission) {
      productItems.links.push({
        label: "Order Flow",
        href: "/order-flow",
        activeRegex: createLeadingPathRegex("/order-flow", { wildcardPathEnd: true }),
      });
    }

    const salesItems: NavBarLink = {
      label: "Sales",
      icon: IconBriefcase,
      links: [
        hasSalesPerformanceViewPermission
          ? {
              label: "Performance",
              href: "/reports/sales-performance",
              activeRegex: createLeadingPathRegex("/reports/sales-performance", { wildcardPathEnd: true }),
            }
          : null,
        {
          label: "Portfolio",
          href: `/portfolio${
            portfolioViewBy
              ?.map(view => `viewBy=${view}`)
              .join("&")
              .replace(/^/, "?") ?? ""
          }`,
          activeRegex: createLeadingPathRegex("/portfolio", { wildcardPathEnd: true }),
        },
        hasCustomerViewPermission
          ? { label: "Customers", href: "/customers", activeRegex: createLeadingPathRegex("/customers", { wildcardPathEnd: true }) }
          : null,
        hasCustomerViewPermission
          ? { label: "Contacts", href: "/contacts", activeRegex: createLeadingPathRegex("/contacts", { wildcardPathEnd: true }) }
          : null,
        {
          label: "Pipeline",
          href: "/opportunities",
          activeRegex: createLeadingPathRegex("/opportunities", { wildcardPathEnd: true }),
        },
        {
          label: "Notes",
          href: "/notes",
          activeRegex: NOTES_NAV_ACTIVE_REGEX,
        },
        hasSalesOrderViewPermission
          ? { label: "Sales Orders", href: "/orders", activeRegex: createLeadingPathRegex("/orders", { wildcardPathEnd: true }) }
          : null,
      ].filter(Boolean) as NavBarLink["links"],
    };

    // SC-2390: the sink-org inbox of purchase orders a counterparty org confirmed, awaiting review
    // before they become draft Sales Orders. No feature flag: enablement is data-driven (no routing
    // rule, no proposals), and the count badge lives on the page header, not here.
    //
    const financeItems: NavBarLink = {
      label: "Accounting",
      icon: IconCurrencyDollar,
      links: [],
    };

    for (const link of buildReceivablePayableNavLinks({
      hasAccountsReceivableViewPermission,
      hasAccountsPayableViewPermission,
    })) {
      financeItems.links.push({
        label: link.label,
        href: link.href,
        activeRegex: createLeadingPathRegex(link.activePath, { wildcardPathEnd: true }),
      });
    }
    if (hasLedgerAccountViewPermission) {
      financeItems.links.push({
        label: "Accounts",
        href: "/accounting/accounts",
        activeRegex: createLeadingPathRegex("/accounting/accounts", { wildcardPathEnd: false }),
      });
    }
    if (hasLedgerAccountViewPermission && isMultiCurrencyEnabled) {
      financeItems.links.push({
        label: "Exchange Rates",
        href: "/accounting/exchange-rates",
        activeRegex:
          /^\/(?:\[orgSlug\]\/)?(?:settings\/)?accounting\/(?:exchange-rates|calendar-periods|revalue-period|fx-adjustments)(?:\/|$)/,
      });
    }
    if (hasIncomeStatementPermission) {
      financeItems.links.push({
        label: "Income Statement",
        href: "/accounting/income-statement",
        activeRegex: createLeadingPathRegex("/accounting/income-statement", { wildcardPathEnd: true }),
      });
    }
    if (hasLedgerTransactionViewPermission) {
      financeItems.links.push({
        label: "Transactions",
        href: "/accounting/transactions",
        activeRegex: createLeadingPathRegex("/accounting/transactions", { wildcardPathEnd: true }),
      });
    }
    if (hasReconciliationViewPermission) {
      financeItems.links.push({
        label: "Reconciliations",
        href: "/accounting/reconciliations",
        activeRegex: createLeadingPathRegex("/accounting/reconciliations", { wildcardPathEnd: true }),
      });
    }
    if (hasReportViewPermission) {
      financeItems.links.push({
        label: "Reports",
        href: "/accounting/reports",
        activeRegex: createLeadingPathRegex("/accounting/reports", { wildcardPathEnd: true }),
      });
    }

    const reportsItems: NavBarLink = {
      label: "Reports",
      icon: IconFileReport,
      links: [
        {
          label: "Sales Orders List",
          href: "/reports/sales-orders",
          activeRegex: createLeadingPathRegex("/reports/sales-orders", { wildcardPathEnd: true }),
        },
      ],
    };

    // AI Agents section - gated by feature flags
    const aiAgentsItems: NavBarLink = {
      label: "AI Agents",
      icon: IconSparkles,
      links: [],
    };

    if (isAssistantEnabled) {
      aiAgentsItems.links.push({
        label: "Assistant",
        href: "/assistant",
        activeRegex: createLeadingPathRegex("/assistant", { wildcardPathEnd: true }),
      });
    }
    if (isStatementMatchEnabled) {
      aiAgentsItems.links.push({
        label: "Statement Match",
        href: "/statement-match",
        activeRegex: createLeadingPathRegex("/statement-match", { wildcardPathEnd: true }),
      });
    }

    const multiOrgItems: NavBarLink = {
      label: "Multi Org",
      icon: IconBuilding,
      links: [
        {
          label: "Workspace",
          href: "/multi-org-workspace",
          activeRegex: createLeadingPathRegex("/multi-org-workspace", { wildcardPathEnd: true }),
        },
      ],
    };

    const navSections = [productItems, salesItems, financeItems];
    if (hasMultiOrgWorkspaceAccess) {
      navSections.push(multiOrgItems);
    }
    if (isReportsV2Enabled) {
      navSections.push(reportsItems);
    }
    if (aiAgentsItems.links.length > 0) {
      navSections.push(aiAgentsItems);
    }

    return navSections.filter(x => !!x.links.length);
  }, [
    hasAccountsPayableViewPermission,
    hasAccountsReceivableViewPermission,
    hasCustomerViewPermission,
    hasLedgerAccountViewPermission,
    hasLedgerTransactionViewPermission,
    hasLotViewPermission,
    hasProductsViewAllPermission,
    hasPurchaseOrderViewPermission,
    hasPricingEngineViewPermission,
    hasReconciliationViewPermission,
    hasReportViewPermission,
    hasIncomeStatementPermission,
    hasSalesOrderViewPermission,
    hasSalesPerformanceViewPermission,
    hasSupplierViewPermission,
    hasWorkOrderViewPermission,
    portfolioViewBy,
    priceBookViewBy,
    isReportsV2Enabled,
    isAssistantEnabled,
    isStatementMatchEnabled,
    workOrderLinks,
    isMultiCurrencyEnabled,
    hasMultiOrgWorkspaceAccess,
  ]);

  // Only a stored `true` collapses: any other stored value, including one that does not parse, reads as open.
  // The Settings menu always shows in full and never changes the saved choice.
  const collapsed = isDesktop && savedCollapsed === true && !showSettings;

  useHotkeys([
    [
      "[",
      () => {
        if (!isDesktop || showSettings) {
          return;
        }
        toggleCollapsed();
      },
    ],
  ]);

  const currentGroupLabel = useMemo(() => currentNavGroupLabel(coreNavLinks, router.pathname), [coreNavLinks, router.pathname]);

  // Expanding opens the current page's section in the same update, so the sidebar renders once per toggle.
  const toggleCollapsed = () => {
    if (collapsed && currentGroupLabel !== null && !navbarSelections.includes(currentGroupLabel)) {
      setNavbarSelections(selections => withOpenGroup(selections, currentGroupLabel));
    }
    setSavedCollapsed(value => value !== true);
  };

  // On desktop, going to a page or expanding the sidebar opens the section that holds the current page. Nothing
  // is closed, and a section the user closes stays closed until the page, the sidebar or the current group changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reruns on a page change; closing a section must not rerun it
  useEffect(() => {
    if (!isDesktop || collapsed || currentGroupLabel === null || navbarSelections.includes(currentGroupLabel)) {
      return;
    }
    setNavbarSelections(selections => withOpenGroup(selections, currentGroupLabel));
  }, [router.pathname, collapsed, currentGroupLabel, isDesktop]);

  const openSettings = () => {
    setFocusSettingsSearch(isDesktop);
    setShowSettings(true);
  };

  const settingsNavLinks: SettingsNavBarLink[] = useMemo<SettingsNavBarLink[]>(() => {
    const adminSettings: SettingsNavBarLink = {
      label: "Admin",
      icon: IconBuilding,
      links: [
        {
          label: "Org Details",
          href: "/settings/org-details",
          activeRegex: createLeadingPathRegex("/settings/org-details", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.orgDetails,
        },
        {
          label: "Remittance Address",
          href: "/settings/remittance-address",
          activeRegex: createLeadingPathRegex("/settings/remittance-address", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.remittanceAddress,
        },
        {
          label: "User Management",
          href: "/settings/user-management",
          activeRegex: createLeadingPathRegex("/settings/user-management", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.userManagement,
        },
        {
          label: "Integrations",
          href: "/settings/integrations",
          activeRegex: createLeadingPathRegex("/settings/integrations", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.integrations,
        },
        // Admin incident surface for auto-created notes; lives under Settings (founder rule
        // 2026-08-17: no new top-level nav tabs). Route stays /notes/admin-bulk.
        ...(hasNotesAdminManagePermission
          ? [
              {
                label: "Note recovery",
                href: "/notes/admin-bulk",
                activeRegex: createLeadingPathRegex("/notes/admin-bulk", { wildcardPathEnd: true }),
                searchWords: SETTINGS_SEARCH_WORDS.noteRecovery,
              },
            ]
          : []),
        // SC-2215: cross-org routing rules moved onto the Multi Org Workspace page (manage-only).
      ],
    };

    const documentsSettings: SettingsNavBarLink = {
      label: "Documents",
      icon: IconFile,
      links: [
        {
          label: "Invoice",
          href: "/settings/invoice-text",
          activeRegex: createLeadingPathRegex("/settings/invoice-text", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.invoice,
        },
        {
          label: "Purchase Order",
          href: "/settings/po-text",
          activeRegex: createLeadingPathRegex("/settings/po-text", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.purchaseOrder,
        },
        {
          label: "Emergency Info",
          href: "/settings/emergency-info",
          activeRegex: createLeadingPathRegex("/settings/emergency-info", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.emergencyInfo,
        },
        {
          label: "Bill of Lading",
          href: "/settings/bill-of-lading",
          activeRegex: createLeadingPathRegex("/settings/bill-of-lading", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.billOfLading,
        },
        ...(hasDocumentsSettingsPermission
          ? [
              {
                label: "SO Confirmation",
                href: "/settings/sales-order-confirmation",
                activeRegex: createLeadingPathRegex("/settings/sales-order-confirmation", { wildcardPathEnd: true }),
                searchWords: SETTINGS_SEARCH_WORDS.soConfirmation,
              },
              {
                label: "Packing List",
                href: "/settings/packing-list",
                activeRegex: createLeadingPathRegex("/settings/packing-list", { wildcardPathEnd: true }),
                searchWords: SETTINGS_SEARCH_WORDS.packingList,
              },
              {
                label: "Warehouse Release",
                href: "/settings/warehouse-release",
                activeRegex: createLeadingPathRegex("/settings/warehouse-release", { wildcardPathEnd: true }),
                searchWords: SETTINGS_SEARCH_WORDS.warehouseRelease,
              },
              {
                label: "Pick Up Request",
                href: "/settings/pick-up-request",
                activeRegex: createLeadingPathRegex("/settings/pick-up-request", { wildcardPathEnd: true }),
                searchWords: SETTINGS_SEARCH_WORDS.pickUpRequest,
              },
              {
                label: "Document Options",
                href: "/settings/document-options",
                activeRegex: createLeadingPathRegex("/settings/document-options", { wildcardPathEnd: true }),
                searchWords: SETTINGS_SEARCH_WORDS.documentOptions,
              },
              {
                label: "Border Crossing",
                href: "/settings/border-crossing",
                activeRegex: createLeadingPathRegex("/settings/border-crossing", { wildcardPathEnd: true }),
                searchWords: SETTINGS_SEARCH_WORDS.borderCrossing,
              },
            ]
          : []),
        ...optInDocumentSettingsLinks,
      ],
    };

    const emailSettings: SettingsNavBarLink = {
      label: "Email",
      icon: IconMail,
      links: [
        {
          label: "Email Templates",
          href: "/settings/emails",
          activeRegex: createLeadingPathRegex("/settings/emails", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.emailTemplates,
        },
        {
          label: "Sent Emails",
          href: "/settings/sent-emails",
          activeRegex: createLeadingPathRegex("/settings/sent-emails", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.sentEmails,
        },
      ],
    };

    const systemSettings: SettingsNavBarLink = {
      label: "System",
      icon: IconListDetails,
      links: [
        {
          label: "Certification Types",
          href: "/settings/cert-types",
          activeRegex: createLeadingPathRegex("/settings/cert-types", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.certificationTypes,
        },
        {
          label: "Automations",
          href: AUTOMATIONS_BASE_PATH,
          activeRegex: createLeadingPathRegex(AUTOMATIONS_BASE_PATH, { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.automations,
        },
        /**
         * Custom Outputs (SC-3493, moved here and renamed in SC-3654).
         *
         * It began in the Products sub-nav and moved to Settings > System per the agreement with
         * Michael: designing what prints is configuration.
         *
         * "Custom Outputs", not "Templates": the feature already covers documents and labels and
         * is expected to grow past both. "Output template" remains the internal name for the model
         * and the module.
         *
         * The two gates are unchanged by the move. The beta flag decides whether the org sees the
         * feature at all; the permission decides who inside that org does. Note that landing in
         * Settings adds the surrounding group's `viewSettings` requirement — see the note where
         * `navItems` is assembled. The router enforces `outputTemplates.access` regardless, so the
         * link is presentation rather than security.
         *
         * The href comes from `OUTPUT_TEMPLATES_BASE_PATH` rather than a literal, because
         * `BETA_NAV_HREFS` matches the beta dot on this exact string: a second spelling here is how
         * the dot silently stops appearing. The old `/templates` prefix redirects to it.
         */
        ...(isOutputTemplatesEnabled && hasOutputTemplatesPermission
          ? [
              {
                label: "Custom Outputs",
                href: OUTPUT_TEMPLATES_BASE_PATH,
                activeRegex: createLeadingPathRegex(OUTPUT_TEMPLATES_BASE_PATH, { wildcardPathEnd: true }),
                searchWords: SETTINGS_SEARCH_WORDS.customOutputs,
              },
            ]
          : []),
        {
          label: "Identifiers",
          href: "/settings/identifiers",
          activeRegex: createLeadingPathRegex("/settings/identifiers", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.identifiers,
        },
        {
          label: "Warehouses",
          href: "/settings/warehouses",
          activeRegex: createLeadingPathRegex("/settings/warehouses", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.warehouses,
        },
        {
          label: "Sales People",
          href: "/settings/sales-people",
          activeRegex: createLeadingPathRegex("/settings/sales-people", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.salesPeople,
        },
        {
          label: "Customer Service Representatives",
          href: "/settings/customer-service-representatives",
          activeRegex: createLeadingPathRegex("/settings/customer-service-representatives", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.customerServiceRepresentatives,
        },
        {
          label: "Supplier Representatives",
          href: "/settings/supplier-representatives",
          activeRegex: createLeadingPathRegex("/settings/supplier-representatives", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.supplierRepresentatives,
        },
        {
          label: "Shipping Options",
          href: "/settings/lookups",
          activeRegex: createLeadingPathRegex("/settings/lookups", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.shippingOptions,
        },
        {
          label: "Tags",
          href: "/settings/system/tags",
          activeRegex: createLeadingPathRegex("/settings/system/tags", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.tags,
        },
        {
          label: "Tax Settings",
          href: "/settings/tax-settings",
          activeRegex: createLeadingPathRegex("/settings/tax-settings", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.taxSettings,
        },
        {
          label: "Lot States",
          href: "/settings/system/lot-states",
          activeRegex: createLeadingPathRegex("/settings/system/lot-states", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.lotStates,
        },
        {
          label: "Inventory Management",
          href: "/settings/system/inventory-management",
          activeRegex: createLeadingPathRegex("/settings/system/inventory-management", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.inventoryManagement,
        },
      ],
    };

    const productSettings: SettingsNavBarLink = {
      label: "Products",
      icon: IconBox,
      links: [
        {
          label: "Product Family Attributes",
          href: "/settings/product-family-attributes",
          activeRegex: createLeadingPathRegex("/settings/product-family-attributes", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.productFamilyAttributes,
        },
        {
          label: "Product Code Attributes",
          href: "/settings/product-code-attributes",
          activeRegex: createLeadingPathRegex("/settings/product-code-attributes", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.productCodeAttributes,
        },
        {
          label: "Package Types",
          href: "/settings/package-types",
          activeRegex: createLeadingPathRegex("/settings/package-types", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.packageTypes,
        },
        {
          label: "Pallets",
          href: "/settings/shipping-platforms",
          activeRegex: createLeadingPathRegex("/settings/shipping-platforms", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.pallets,
        },
      ],
    };

    const salesOrderSettings: SettingsNavBarLink = {
      label: "Sales Orders",
      icon: IconBriefcase,
      links: [],
    };
    if (hasSettingsViewPermission) {
      salesOrderSettings.links.push(
        {
          label: "Check List Items",
          href: "/settings/check-list-items",
          activeRegex: createLeadingPathRegex("/settings/check-list-items", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.checkListItems,
        },
        {
          label: "Blockers",
          href: "/settings/blockers",
          activeRegex: createLeadingPathRegex("/settings/blockers", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.blockers,
        },
      );
    }
    if (hasSettingsViewPermission && isOrderItemLabelEnabled) {
      salesOrderSettings.links.push({
        label: "Sales Order Item Label Templates",
        href: "/settings/order-item-label-templates",
        activeRegex: createLeadingPathRegex("/settings/order-item-label-templates", { wildcardPathEnd: true }),
        searchWords: SETTINGS_SEARCH_WORDS.salesOrderItemLabelTemplates,
      });
    }
    if (hasPricingEngineViewPermission) {
      salesOrderSettings.links.push({
        label: "Margin Settings",
        href: "/settings/margin",
        activeRegex: createLeadingPathRegex("/settings/margin", { wildcardPathEnd: true }),
        searchWords: SETTINGS_SEARCH_WORDS.marginSettings,
      });
    }

    const orderItemTemplatesSettings: SettingsNavBarLink = {
      label: "Order Item Templates",
      icon: IconTemplate,
      links: [
        {
          label: "Sales Order Item Templates",
          href: "/settings/order-item-templates",
          activeRegex: createLeadingPathRegex("/settings/order-item-templates", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.salesOrderItemTemplates,
        },
        {
          label: "Purchase Order Item Templates",
          href: "/settings/purchase-order-item-templates",
          activeRegex: createLeadingPathRegex("/settings/purchase-order-item-templates", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.purchaseOrderItemTemplates,
        },
        {
          label: "Work Order Item Templates",
          href: "/settings/work-order-cost-templates",
          activeRegex: createLeadingPathRegex("/settings/work-order-cost-templates", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.workOrderItemTemplates,
        },
      ],
    };

    const accountingSettings: SettingsNavBarLink = {
      label: "Accounting",
      icon: IconCurrencyDollar,
      links: [
        {
          label: "Accounts",
          href: "/settings/accounting/accounts",
          activeRegex: createLeadingPathRegex("/settings/accounting/accounts", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.accounts,
        },
        {
          label: "Bank Accounts",
          href: "/settings/accounting/bank-accounts",
          activeRegex: createLeadingPathRegex("/settings/accounting/bank-accounts", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.bankAccounts,
        },
        {
          label: "Payment Terms",
          href: "/settings/accounting/payment-terms",
          activeRegex: createLeadingPathRegex("/settings/accounting/payment-terms", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.paymentTerms,
        },
        {
          label: "Transaction Templates",
          href: "/settings/accounting/transaction-templates",
          activeRegex: createLeadingPathRegex("/settings/accounting/transaction-templates", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.transactionTemplates,
        },
      ],
    };

    const accountSettings: SettingsNavBarLink = {
      label: "Account",
      icon: IconUser,
      links: [
        {
          label: "Manage",
          href: "/settings/account/manage",
          activeRegex: createLeadingPathRegex("/settings/account/manage", { wildcardPathEnd: true }),
          searchWords: SETTINGS_SEARCH_WORDS.manage,
        },
      ],
    };

    const navItems: SettingsNavBarLink[] = [];
    /**
     * Settings groups are gated as a whole on `viewSettings`, which Custom Outputs now inherits by
     * living in System (SC-3654). Someone holding `outputTemplates.access` WITHOUT `viewSettings`
     * therefore no longer sees the link, where they did while it sat under Products. That follows
     * from putting it in Settings rather than from anything this change decided; the page itself
     * is unchanged and still enforces `outputTemplates.access` on its own.
     */
    if (hasSettingsViewPermission) {
      navItems.push(adminSettings, documentsSettings, emailSettings, systemSettings, productSettings);
    }

    if (salesOrderSettings.links.length > 0) {
      navItems.push(salesOrderSettings);
    }

    if (hasSettingsViewPermission) {
      navItems.push(orderItemTemplatesSettings);

      if (
        hasReportViewPermission ||
        hasReconciliationViewPermission ||
        hasLedgerTransactionViewPermission ||
        hasLedgerAccountViewPermission
      ) {
        navItems.push(accountingSettings);
      }
    }

    if (hasPlatformBillingViewPermission && platformBillingAvailability.data) {
      navItems.push({
        label: "Billing",
        icon: IconCurrencyDollar,
        links: [
          {
            label: "Subscription & Billing",
            href: "/settings/subscription-billing",
            activeRegex: createLeadingPathRegex("/settings/subscription-billing", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.subscriptionAndBilling,
          },
        ],
      });
    }

    navItems.push(accountSettings);

    if (isSuperAdmin) {
      navItems.push({
        label: "Super Admin",
        icon: IconKey,
        links: [
          {
            label: "Org Management",
            href: "/settings/super/org-management",
            activeRegex: createLeadingPathRegex("/settings/super/org-management", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superOrgManagement,
          },
          {
            label: "Roles",
            href: "/settings/super/roles-management",
            activeRegex: createLeadingPathRegex("/settings/super/roles-management", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superRoles,
          },
          {
            label: "Org Settings",
            href: "/settings/super/org-settings",
            activeRegex: createLeadingPathRegex("/settings/super/org-settings", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superOrgSettings,
          },
          {
            label: "Physical Checks",
            href: "/settings/super/physical-checks",
            activeRegex: createLeadingPathRegex("/settings/super/physical-checks", { wildcardPathEnd: true }),
            searchWords: ["physical check", "voucher check", "pay to the order of", "check stock"],
          },
          {
            label: "API Keys",
            href: "/settings/super/api-keys",
            activeRegex: createLeadingPathRegex("/settings/super/api-keys", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superApiKeys,
          },
          ...(isPlaidBankFeedEnabled
            ? [
                {
                  label: "Plaid Setup",
                  href: "/settings/super/plaid-setup",
                  activeRegex: createLeadingPathRegex("/settings/super/plaid-setup", { wildcardPathEnd: true }),
                  searchWords: SETTINGS_SEARCH_WORDS.superPlaidSetup,
                },
              ]
            : []),
          {
            label: "Importer",
            href: "/settings/super/importer",
            activeRegex: createLeadingPathRegex("/settings/super/importer", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superImporter,
          },
          {
            label: "Weight Fixer",
            href: "/settings/super/weightFixer",
            activeRegex: createLeadingPathRegex("/settings/super/weightFixer", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superWeightFixer,
          },
          {
            label: "Lot Fixer",
            href: "/settings/super/lotFixer",
            activeRegex: createLeadingPathRegex("/settings/super/lotFixer", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superLotFixer,
          },
          {
            label: "Cost Flow Checker",
            href: "/settings/super/costFlowChecker",
            activeRegex: createLeadingPathRegex("/settings/super/costFlowChecker", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superCostFlowChecker,
          },
          {
            label: "Inventory Management",
            href: "/settings/super/inventoryManagement",
            activeRegex: createLeadingPathRegex("/settings/super/inventoryManagement", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superInventoryManagement,
          },
          {
            label: "Order KPI Fixer",
            href: "/settings/super/orderKpiFixer",
            activeRegex: createLeadingPathRegex("/settings/super/orderKpiFixer", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superOrderKpiFixer,
          },
          {
            label: "AI Embeddings",
            href: "/settings/super/ai-embeddings",
            activeRegex: createLeadingPathRegex("/settings/super/ai-embeddings", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superAiEmbeddings,
          },
          {
            label: "Parked inbound email",
            href: "/settings/super/parked-emails",
            activeRegex: createLeadingPathRegex("/settings/super/parked-emails", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superParkedInboundEmail,
          },
          {
            label: "Subledger diagnostics",
            href: "/settings/super/subledger-view",
            activeRegex: createLeadingPathRegex("/settings/super/subledger-view", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superSubledgerDiagnostics,
          },
          /**
           * NOT labelled "Custom Outputs", deliberately (SC-4044).
           *
           * That label and the `customOutputs` search key already belong to the customer-facing
           * link above, and two rows answering one search with different destinations is worse
           * than a longer name. This one says what it actually operates on — ShelfCycle's base
           * templates in the platform org — which is also the distinction an operator needs.
           *
           * No feature flag: the super-admin group is already behind `isSuperAdmin`, and the base
           * has to be publishable in an environment before any org's flag could matter.
           */
          {
            label: "Base Output Templates",
            href: "/settings/super/base-output-templates",
            activeRegex: createLeadingPathRegex("/settings/super/base-output-templates", { wildcardPathEnd: true }),
            searchWords: SETTINGS_SEARCH_WORDS.superBaseOutputTemplates,
          },
        ],
      });
    }

    return navItems;
  }, [
    isSuperAdmin,
    hasReportViewPermission,
    hasReconciliationViewPermission,
    hasLedgerTransactionViewPermission,
    hasLedgerAccountViewPermission,
    hasSettingsViewPermission,
    hasNotesAdminManagePermission,
    hasDocumentsSettingsPermission,
    hasPlatformBillingViewPermission,
    hasPricingEngineViewPermission,
    platformBillingAvailability.data,
    isOrderItemLabelEnabled,
    isPlaidBankFeedEnabled,
    optInDocumentSettingsLinks,
    /**
     * Both gates for Custom Outputs, which moved into this memo with the link (SC-3654).
     *
     * `isOutputTemplatesEnabled` especially: a LaunchDarkly flag resolves ASYNCHRONOUSLY on the
     * client, so it is false on the first render and flips once the SDK answers. Leaving it out of
     * this array would memoise the nav built from that first false and the link would never
     * appear — the same class of disappearing-nav bug this feature has already had once.
     */
    isOutputTemplatesEnabled,
    hasOutputTemplatesPermission,
  ]);

  const navLinkLabel = (link: NavBarLink["links"][number]): React.ReactNode =>
    BETA_NAV_HREFS.has(link.href) ? (
      <Flex align="center" gap="sm" justify="space-between" w="100%">
        {link.label}
        <BetaDot />
      </Flex>
    ) : (
      link.label
    );

  const commonNavLinkProps = (link: NavBarLink["links"][number]) => ({
    disabled: link.disabled,
    label: navbarOpened ? navLinkLabel(link) : undefined,
    href: link.href,
    onClick: !isDesktop ? onClose : undefined,
    linkComponent: OrgLink,
    active: link.activeRegex.test(router.pathname),
    className: navbarStyles.navLink,
  });

  const visibleSettingsNavLinks = useMemo(() => filterSettingsNavLinks(settingsNavLinks, settingsQuery), [settingsNavLinks, settingsQuery]);

  const settingsNavLinkLabel = (link: SettingsNavLink): React.ReactNode =>
    link.searchHint ? (
      <Flex direction="column" gap={2}>
        {navLinkLabel(link)}
        <Text size="xs" color={mantineColorRamps.gray[7]}>
          {link.searchHint}
        </Text>
      </Flex>
    ) : (
      navLinkLabel(link)
    );

  // Choosing a Settings page clears the search, including the page already open, which does not navigate.
  const settingsNavLinkProps = (link: SettingsNavLink) => ({
    ...commonNavLinkProps(link),
    label: navbarOpened ? settingsNavLinkLabel(link) : undefined,
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
      const chosenLink = event.currentTarget;
      const wasSearching = settingsQuery.trim() !== "";
      setSettingsQuery("");
      if (!isDesktop) {
        onClose();
      } else if (wasSearching) {
        // The full menu comes back with the chosen page in view, not wherever the narrowed list left the scroll.
        window.requestAnimationFrame(() => chosenLink.scrollIntoView({ block: "center" }));
      }
    },
  });

  return (
    <PrimaryNavigation collapsed={collapsed}>
      <PrimaryNavigationSection>
        <Flex
          p="xs"
          direction="row"
          h={APPLICATION_FRAME_HEADER_HEIGHT}
          w="100%"
          style={{
            borderBottom: `1px solid ${tokens.color.surface.raised}`,
          }}
        >
          <AccountActions
            compact={collapsed}
            closeNavbar={
              isDesktop
                ? () => {
                    return;
                  }
                : onClose
            }
            toggleSettingsNavbar={openSettings}
          />
        </Flex>
      </PrimaryNavigationSection>
      {showSettings ? (
        <>
          <PrimaryNavigationSection>
            <PrimaryNavigationLink
              tone="muted"
              icon={<IconCaretLeft size={20} stroke={1.5} color="black" />}
              label="Settings"
              onClick={() => setShowSettings(false)}
              className={navbarStyles.settingsBackLink}
            />
            <TextInput
              placeholder="Search settings..."
              aria-label="Search settings"
              autoFocus={focusSettingsSearch}
              icon={<IconSearch size={16} />}
              px="sm"
              pt={4}
              value={settingsQuery}
              onChange={event => changeSettingsQuery(event.currentTarget.value)}
            />
          </PrimaryNavigationSection>
          <PrimaryNavigationSection scrollable viewportRef={settingsScrollViewportRef}>
            {visibleSettingsNavLinks.length === 0 ? (
              <Text size="sm" c="dimmed" px="md">
                Nothing matches “{settingsQuery}”.
              </Text>
            ) : (
              <Accordion multiple value={visibleSettingsNavLinks.map(nl => nl.label)} itemClassName={navbarStyles.accordionItem}>
                {visibleSettingsNavLinks.map(nav => (
                  <Accordion.Item key={nav.label} value={nav.label}>
                    <Accordion.Control chevron={<></>}>
                      <Group noWrap>
                        {nav.icon && <nav.icon size={20} stroke={1.5} />}
                        {nav.label}
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel ml="28px" style={{ borderLeft: `2px solid ${tokens.color.surface.raised}` }}>
                      {nav.links.map(link => (
                        <PrimaryNavigationLink key={link.label} {...settingsNavLinkProps(link)} />
                      ))}
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}
          </PrimaryNavigationSection>
        </>
      ) : collapsed ? (
        <PrimaryNavigationSection scrollable>
          {coreNavLinks.map(nav => (
            <PrimaryNavigationRailGroup
              key={nav.label}
              current={nav.label === currentGroupLabel}
              icon={<nav.icon size={20} stroke={1.5} />}
              label={nav.label}
            >
              {nav.links.map(link => (
                <PrimaryNavigationRailMenuLink
                  key={link.label}
                  active={link.activeRegex.test(router.pathname)}
                  href={link.href}
                  label={navLinkLabel(link)}
                  linkComponent={OrgLink}
                />
              ))}
            </PrimaryNavigationRailGroup>
          ))}
        </PrimaryNavigationSection>
      ) : (
        <PrimaryNavigationSection scrollable>
          <Accordion multiple value={navbarSelections} onChange={setNavbarSelections} itemClassName={navbarStyles.accordionItem}>
            {coreNavLinks.map(nav => (
              <Accordion.Item key={nav.label} value={nav.label}>
                <Accordion.Control>
                  <Group noWrap>
                    {nav.icon && <nav.icon size={20} stroke={1.5} />}
                    {nav.label}
                  </Group>
                </Accordion.Control>
                <Accordion.Panel ml="28px" style={{ borderLeft: `2px solid ${tokens.color.surface.raised}` }}>
                  {nav.links.map(link => (
                    <PrimaryNavigationLink key={link.label} {...commonNavLinkProps(link)} />
                  ))}
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </PrimaryNavigationSection>
      )}
      <PrimaryNavigationFooter>
        {hasSettingsViewPermission && (
          <PrimaryNavigationButton
            aria-pressed={showSettings}
            current={showSettings || onSettingsPage}
            icon={<IconSettings size={20} stroke={1.5} />}
            label="Settings"
            onClick={() => (showSettings ? setShowSettings(false) : openSettings())}
          />
        )}
        {isDesktop && !showSettings && <PrimaryNavigationCollapseButton collapsed={collapsed} onToggle={toggleCollapsed} />}
      </PrimaryNavigationFooter>
    </PrimaryNavigation>
  );
};
